import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { BarChart3, LogOut, CreditCard, Download, Key, Calendar, CheckCircle, AlertCircle, Clock, XCircle, Settings, RefreshCw, MessageCircle, Star } from 'lucide-react';
import FeedbackModal from './FeedbackModal';
import PortfolioAuditorLogo from './FundWiseLogo';

const Dashboard = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPayment, setShowPayment] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  // Pagination and search state for orders
  const [ordersPagination, setOrdersPagination] = useState({
    currentPage: 1,
    itemsPerPage: 5
  });
  const [orderSearchFilters, setOrderSearchFilters] = useState({
    orderId: '',
    licenseKey: '',
    paymentId: '',
    package: ''
  });
  
  // Feedback-related state
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [canSubmitToday, setCanSubmitToday] = useState(true);
  const [hasRecentFeedback, setHasRecentFeedback] = useState(false);
  const [showFeedbackPrompt, setShowFeedbackPrompt] = useState(false);
  
  // S3 Download state
  const [downloadingOrders, setDownloadingOrders] = useState(new Set());
  
  // Subscription management state
  const [subscriptions, setSubscriptions] = useState([]);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [togglingAutoRenew, setTogglingAutoRenew] = useState(new Set());
  
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  // Styled notification function
  const showNotification = (type, title, message) => {
    setNotification({ type, title, message });
    setTimeout(() => setNotification(null), 6000); // Auto-hide after 6 seconds
  };

  const pricingPlans = [
    {
      id: 'beta',
      name: 'Beta Free Trial',
      price: parseInt(process.env.REACT_APP_BETA_PRICE || '0'),
      period: `${process.env.REACT_APP_BETA_DURATION || '7-day trial'}`,
      tag: 'TRY FOR FREE',
      description: `Experience full power of Portfolio Auditor PC Desktop Tool with ${process.env.REACT_APP_BETA_DURATION || '1-month free trial'} license.`,
      featureSections: [
        {
          title: "Portfolio vs Benchmark",
          features: [
            "Single PAN Investment Analysis",
            "Portfolio vs Standard Benchmark",
            "Portfolio vs Custom Fund Benchmark",
            "Outperformance/Underperformance Visualization charts",
            "Clear Insights on underperforming Fund",
            "Fully Customizable Dashboard"
          ]
        },
        {
          title: "Daily Investment Intelligence",
          features: [
            "Daily Portfolio Tracking, Investment Summary",
            "Tax Calculation, Rolling Return Analysis, NAV Purchase Analysis"
          ]
        },
        {
          title: "Support & Updates",
          features: [
            "Live Chat & Email Support",
            "Free Future Tool Enhancements"
          ]
        }
      ],
      beta: true,
      buttonText: `Get Free License for ${process.env.REACT_APP_BETA_PERIOD || '1 Month'}`
    },
    {
      id: 'individual', 
      name: 'Monthly Plan',
      price: parseInt(process.env.REACT_APP_INDIVIDUAL_PRICE || '199'),
      period: '/mo',
      tag: 'BEST FOR SOLO INVESTORS',
      description: 'PC Desktop Tool License - Monthly auto-renewal for individual portfolio analysis on your PC.',
      featureSections: [
        {
          title: "Portfolio vs Benchmark",
          features: [
            "Single PAN Investment Analysis",
            "Portfolio vs Standard Benchmark",
            "Portfolio vs Custom Fund Benchmark",
            "Outperformance/Underperformance Visualization charts",
            "Clear Insights on underperforming Fund",
            "Fully Customizable Dashboard"
          ]
        },
        {
          title: "Daily Investment Intelligence",
          features: [
            "Daily Portfolio Tracking, Investment Summary",
            "Tax Calculation, Rolling Return Analysis, NAV Purchase Analysis"
          ]
        },
        {
          title: "Support & Updates",
          features: [
            "Live Chat & Email Support",
            "Free Future Tool Enhancements"
          ]
        }
      ],
      popular: false,
      buttonText: `Get License for ₹${process.env.REACT_APP_INDIVIDUAL_PRICE || '199'}/mo`
    },
    {
      id: 'family',
      name: 'Family',
      price: parseInt(process.env.REACT_APP_FAMILY_PRICE || '299'),
      period: '/mo',
      tag: 'BEST VALUE',
      description: 'PC Desktop Tool License - Monthly auto-renewal for combined family portfolio analysis on your PC.',
      savings: `Save ₹${(3 * parseInt(process.env.REACT_APP_INDIVIDUAL_PRICE || '199')) - parseInt(process.env.REACT_APP_FAMILY_PRICE || '299')}/mo vs 3 Individual Plans`,
      featureSections: [
        {
          title: "Multi-PAN Analysis",
          features: [
            `**Up to ${process.env.REACT_APP_FAMILY_PAN_LIMIT || '3'} PAN Combined Analysis**`,
            "Everything in Monthly Plan",
            "Family Portfolio vs Benchmark Comparison",
            "Consolidated Family Portfolio View"
          ]
        },
        {
          title: "Priority Support & Updates",
          features: [
            "Priority Support & Faster Response",
            "Free Future Tool Enhancements"
          ]
        }
      ],
      popular: false,
      buttonText: `Get License for ₹${process.env.REACT_APP_FAMILY_PRICE || '299'}/mo`
    },
    {
      id: 'annual',
      name: 'Yearly Plan',
      price: parseInt(process.env.REACT_APP_ANNUAL_PRICE || '699'),
      period: '/year',
      tag: 'MAXIMUM SAVINGS',
      description: 'PC Desktop Tool License - Annual subscription with maximum savings for Individual or combined family portfolio analysis on your PC.',
      savings: `Save ₹${(12 * parseInt(process.env.REACT_APP_INDIVIDUAL_PRICE || '99')) - parseInt(process.env.REACT_APP_ANNUAL_PRICE || '699')}/year vs Monthly Individual Plan`,
      featureSections: [
        {
          title: "Multi-PAN Analysis",
          features: [
            `**Up to ${process.env.REACT_APP_ANNUAL_PAN_LIMIT || '3'} PAN Combined Analysis**`,
            "Everything in Monthly Plan",
            "Family Portfolio vs Benchmark Comparison",
            "Consolidated Family Portfolio View"
          ]
        },
        {
          title: "Priority Support & Updates",
          features: [
            "Priority Support & Faster Response",
            "Free Future Tool Enhancements",
            "365 Days Validity"
          ]
        }
      ],
      popular: true,
      buttonText: `Get License for ₹${process.env.REACT_APP_ANNUAL_PRICE || '699'}/year`
    }
  ];

  useEffect(() => {
    fetchOrders();
    fetchSubscriptions();
  }, []);

  useEffect(() => {
    checkFeedbackStatus();
  }, [orders]); // Check feedback status after orders are loaded

  const checkFeedbackStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/feedback/check`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setCanSubmitToday(response.data.can_submit_today);
      setHasRecentFeedback(response.data.has_recent_feedback);
      
      // Show periodic prompt if hasn't given recent feedback and has been using for a while
      if (!response.data.has_recent_feedback && orders.length > 0) {
        // Show prompt after 3 seconds if user has orders (been using the system)
        setTimeout(() => {
          setShowFeedbackPrompt(true);
        }, 3000);
      }
    } catch (error) {
      console.error('Failed to check feedback status:', error);
    }
  };

  const fetchOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/orders/my-orders`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      if (error.response?.status === 401) {
        // Token expired or invalid, redirect to login
        logout();
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptions = async () => {
    try {
      setSubscriptionsLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/user/subscriptions`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.data.success) {
        setSubscriptions(response.data.subscriptions);
      }
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      if (error.response?.status === 401) {
        logout();
        navigate('/login');
      }
    } finally {
      setSubscriptionsLoading(false);
    }
  };

  const handlePurchase = async (planId) => {
    setSelectedPlan(planId);
    setPaymentLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      // Create order
      const orderResponse = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/orders/create`, {
        subscription_type: planId
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const { order_id, razorpay_order_id, subscription_id, subscription_short_url, amount, currency, is_beta_completed, is_subscription } = orderResponse.data;
      
      // Check if this is a beta trial that was immediately completed
      if (is_beta_completed) {
        // Beta trial completed immediately, no payment needed
        await fetchOrders();
        showNotification('success', 'Beta Trial Activated!', 'Free beta trial activated successfully! Check your orders for the download link and license key.');
        return;
      }
      
      // Check if this is a subscription (Individual/Family plans)
      if (is_subscription && subscription_id) {
        // NEW FLOW: Use Razorpay modal with subscription_id
        // This gives us proper callbacks for success and dismissal
        
        const razorpayOptions = {
          key: process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_test_11111111111111',
          subscription_id: subscription_id,  // Pass subscription ID to modal
          name: 'Portfolio Auditor Software',
          description: `${pricingPlans.find(p => p.id === planId)?.name} Subscription`,
          
          // SUCCESS HANDLER - Called when payment succeeds
          handler: async function (response) {
            try {
              console.log('✅ Payment successful:', response);
              
              const token = localStorage.getItem('token');
              
              // Call backend to verify and create order (silently in background)
              const verifyResponse = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/orders/verify-and-create`,
                {
                  razorpay_order_id: response.razorpay_subscription_id,  // This is subscription_id
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature
                },
                {
                  headers: {
                    'Authorization': `Bearer ${token}`
                  }
                }
              );
              
              console.log('✅ Order created:', verifyResponse.data);
              
              // Show friendly info notification AFTER verification succeeds
              showNotification(
                'info',
                'Payment Successful!',
                'Your license and download link generation in Progress and will be available in a couple of minutes. Please refresh your orders post 2 Mins. Sorry for the delay.'
              );
              
              // Refresh orders after a short delay to allow backend processing
              setTimeout(async () => {
                await fetchOrders();
              }, 2000);
              
            } catch (error) {
              console.error('❌ Payment verification error:', error);
              // Show INFO notification (blue) even for errors to keep user calm
              showNotification(
                'info',
                'Payment Successful!',
                'Your payment was successful. Your license and download link will be available in a couple of seconds. Please refresh your orders below.'
              );
              
              // Still try to refresh orders after delay
              setTimeout(async () => {
                await fetchOrders();
              }, 5000);
            }
          },
          
          // MODAL CLOSE HANDLER - Called when user closes modal without paying
          modal: {
            ondismiss: function() {
              console.log('⚠️ Payment modal closed by user');
              showNotification(
                'info',
                'Payment Cancelled',
                'You closed the payment window. No charges have been made. You can try again anytime.'
              );
            }
          },
          
          prefill: {
            name: user.name,
            email: user.email
          },
          
          theme: {
            color: '#2563eb'
          }
        };
        
        // Open Razorpay modal
        const razorpay = new window.Razorpay(razorpayOptions);
        razorpay.open();
        
        return;
      }
      
      // Fallback: Old order-based payment flow (should not be used for new subscriptions)
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_test_11111111111111',
        amount: amount,
        currency: currency,
          name: 'Portfolio Auditor Software',
          description: `${pricingPlans.find(p => p.id === planId)?.name} Subscription`,
          order_id: razorpay_order_id,
          handler: async function (response) {
            try {
              const token = localStorage.getItem('token');
              // Verify payment
              await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/orders/verify-payment`, {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              }, {
                headers: {
                  'Authorization': `Bearer ${token}`
                }
              });
              
              // Refresh orders
              await fetchOrders();
              const selectedPlanName = pricingPlans.find(p => p.id === planId)?.name;
              showNotification('success', `${selectedPlanName} Plan Purchased!`, `Payment successful! Check your orders for download link and license key.`);
            } catch (error) {
              console.error('Payment verification error:', error);
              showNotification('error', 'Payment Verification Failed', 'Payment verification failed. Please contact support.');
            }
          },
          prefill: {
            name: user.name,
            email: user.email
          },
          theme: {
            color: '#2563eb'
          }
        };
        
        const razorpay = new window.Razorpay(options);
        razorpay.open();
      
    } catch (error) {
      console.error('Order creation error:', error);
      
      // Check if it's a beta trial abuse prevention error (403)
      if (error.response?.status === 403) {
        showNotification('error', 'Beta Trial Already Used', error.response?.data?.detail || 'You have already used your free beta trial. Please choose an individual or family plan to continue using Portfolio Auditor.');
      } else {
        showNotification('error', 'Order Creation Failed', 'Failed to create order. Please try again.');
      }
    } finally {
      setPaymentLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'expired':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'completed':
        return 'order-status-completed';
      case 'pending':
        return 'order-status-pending';
      case 'failed':
        return 'order-status-failed';
      default:
        return 'order-status-cancelled';
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleFeedbackSubmit = async (feedbackData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/feedback`, feedbackData, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setCanSubmitToday(false); // User can't submit again today
        setHasRecentFeedback(true); // User has recent feedback now
        setShowFeedbackPrompt(false);
        showNotification('success', 'Thank you! 🎉', 'Your feedback has been submitted successfully. We appreciate your input!');
      }
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      if (error.response?.data?.detail) {
        showNotification('error', 'Feedback Error', error.response.data.detail);
      } else {
        showNotification('error', 'Feedback Error', 'Failed to submit feedback. Please try again.');
      }
    }
  };

  // Filter and sort orders
  const getFilteredAndSortedOrders = () => {
    let filtered = orders.filter(order => {
      const orderIdMatch = order.id.toLowerCase().includes(orderSearchFilters.orderId.toLowerCase());
      const licenseKeyMatch = (order.license_key || '').toLowerCase().includes(orderSearchFilters.licenseKey.toLowerCase());
      const paymentIdMatch = (order.razorpay_payment_id || '').toLowerCase().includes(orderSearchFilters.paymentId.toLowerCase());
      const packageMatch = order.subscription_type.toLowerCase().includes(orderSearchFilters.package.toLowerCase());
      
      return orderIdMatch && licenseKeyMatch && paymentIdMatch && packageMatch;
    });
    
    // Sort by purchase date (most recent first)
    filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    
    return filtered;
  };

  // Get paginated orders
  const getPaginatedOrders = () => {
    const filteredOrders = getFilteredAndSortedOrders();
    const startIndex = (ordersPagination.currentPage - 1) * ordersPagination.itemsPerPage;
    const endIndex = startIndex + ordersPagination.itemsPerPage;
    return {
      orders: filteredOrders.slice(startIndex, endIndex),
      totalOrders: filteredOrders.length,
      totalPages: Math.ceil(filteredOrders.length / ordersPagination.itemsPerPage)
    };
  };

  // Handle page change
  const handleOrderPageChange = (newPage) => {
    setOrdersPagination(prev => ({ ...prev, currentPage: newPage }));
  };

  // Handle S3 secure download
  const handleSecureDownload = async (orderId) => {
    console.log('🔥 handleSecureDownload called with orderId:', orderId);
    try {
      // Add order to downloading state
      console.log('🔄 Setting downloading state for order:', orderId);
      setDownloadingOrders(prev => new Set([...prev, orderId]));

      console.log('🌐 Making API call to download-link endpoint...');
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/orders/${orderId}/download-link`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success && response.data.download_url) {
        // Create a temporary download link
        const downloadUrl = response.data.download_url;
        const filename = response.data.file_info?.actual_filename || response.data.file_info?.filename || 'Portfolio-Auditor-Software-Bundle.zip';
        const tempLink = document.createElement('a');
        tempLink.href = downloadUrl;
        tempLink.download = filename;
        document.body.appendChild(tempLink);
        tempLink.click();
        document.body.removeChild(tempLink);

        // Show success notification with expiry info
        const expiresAt = new Date(response.data.expires_at);
        const expiryTime = expiresAt.toLocaleTimeString([], { 
          hour: '2-digit', 
          minute: '2-digit' 
        });
        
        showNotification(
          'success', 
          'Download Started! 🚀', 
          `Your secure download has started. This download link expires at ${expiryTime} today.`
        );
      } else {
        throw new Error('Invalid download response');
      }
    } catch (error) {
      console.error('🔥 Secure download failed:', error);
      console.error('🔥 Error details:', error.response?.status, error.response?.data);
      
      let errorMessage = 'Failed to generate secure download link. Please try again.';
      
      if (error.response?.status === 401) {
        // Token expired or invalid - redirect to login
        errorMessage = 'Your session has expired. Please log in again to download.';
        showNotification('error', 'Session Expired', errorMessage);
        
        // Clear the expired token and redirect to login
        localStorage.removeItem('token');
        setTimeout(() => {
          logout(); // This should redirect to login page
        }, 3000);
        return;
      } else if (error.response?.status === 404) {
        errorMessage = 'Order not found or does not belong to your account.';
      } else if (error.response?.status === 400) {
        errorMessage = 'Download is only available for completed orders.';
      } else if (error.response?.status === 403) {
        errorMessage = 'Access denied. Please log in again to continue.';
      } else if (error.response?.status === 503) {
        errorMessage = 'Software download is temporarily unavailable. Please contact support.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      
      showNotification('error', 'Download Error', errorMessage);
    } finally {
      // Remove order from downloading state
      setDownloadingOrders(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderId);
        return newSet;
      });
    }
  };

  // Reset pagination when filters change
  const handleFilterChange = (filterType, value) => {
    setOrderSearchFilters(prev => ({ ...prev, [filterType]: value }));
    setOrdersPagination(prev => ({ ...prev, currentPage: 1 })); // Reset to page 1
  };

  // Clear all filters
  const clearOrderFilters = () => {
    setOrderSearchFilters({ orderId: '', licenseKey: '', paymentId: '', package: '' });
    setOrdersPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  // Toggle auto-renewal for a subscription
  const handleToggleAutoRenew = async (orderId, currentAutoRenew) => {
    try {
      setTogglingAutoRenew(prev => new Set([...prev, orderId]));
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/subscriptions/toggle-auto-renew`,
        {
          order_id: orderId,
          auto_renew_enabled: !currentAutoRenew
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        showNotification(
          'success',
          'Auto-Renewal Updated',
          response.data.message
        );
        // Refresh subscriptions
        await fetchSubscriptions();
      }
    } catch (error) {
      console.error('Error toggling auto-renewal:', error);
      showNotification(
        'error',
        'Update Failed',
        error.response?.data?.detail || 'Failed to update auto-renewal setting'
      );
    } finally {
      setTogglingAutoRenew(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderId);
        return newSet;
      });
    }
  };

  // Show cancel confirmation modal
  const handleCancelClick = (subscription) => {
    setSelectedSubscription(subscription);
    setShowCancelModal(true);
  };

  // Cancel subscription
  const handleCancelSubscription = async () => {
    if (!selectedSubscription) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/subscriptions/cancel`,
        {
          order_id: selectedSubscription.order_id
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        showNotification(
          'success',
          'Subscription Cancelled',
          response.data.message
        );
        // Refresh subscriptions and orders
        await fetchSubscriptions();
        await fetchOrders();
        setShowCancelModal(false);
        setSelectedSubscription(null);
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      showNotification(
        'error',
        'Cancellation Failed',
        error.response?.data?.detail || 'Failed to cancel subscription'
      );
    }
  };

  const paginatedOrdersData = getPaginatedOrders();

  return (
    <div className="min-h-screen bg-deep-space">
      {/* Notification Component */}
      {notification && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 transition-all duration-300 ease-in-out">
          <div className={`w-96 max-w-full bg-white/10 backdrop-blur-xl shadow-xl rounded-xl pointer-events-auto flex ring-1 ring-white/20 ${
            notification.type === 'success' ? 'border-l-4 border-green-500' : 
            notification.type === 'info' ? 'border-l-4 border-blue-500' : 
            'border-l-4 border-red-500'
          }`}>
            <div className="flex-1 w-0 p-5">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  {notification.type === 'success' ? (
                    <CheckCircle className="h-7 w-7 text-green-500" />
                  ) : notification.type === 'info' ? (
                    <AlertCircle className="h-7 w-7 text-blue-500" />
                  ) : (
                    <XCircle className="h-7 w-7 text-red-500" />
                  )}
                </div>
                <div className="ml-4 flex-1">
                  <p className="text-base font-semibold text-white">
                    {notification.title}
                  </p>
                  <p className="mt-2 text-sm text-slate-300 leading-relaxed">
                    {notification.message}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex border-l border-white/20">
              <button
                onClick={() => setNotification(null)}
                className="w-full border border-transparent rounded-none rounded-r-xl p-4 flex items-center justify-center text-sm font-medium text-slate-400 hover:text-white focus:outline-none transition-colors"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-deep-space/80 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-2">
              <PortfolioAuditorLogo size="md" variant="default" />
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm text-slate-400">Welcome back,</p>
                <p className="font-semibold text-white" data-testid="user-name">{user?.name}</p>
              </div>
              {user?.is_admin && (
                <button
                  onClick={() => navigate('/admin')}
                  className="bg-purple-500/20 text-purple-300 px-4 py-2 rounded-lg hover:bg-purple-500/30 transition-colors flex items-center space-x-2 border border-purple-500/30"
                  data-testid="admin-panel-btn"
                >
                  <Settings className="w-4 h-4" />
                  <span>Admin</span>
                </button>
              )}
              <button
                onClick={() => setShowFeedbackModal(true)}
                className={`inline-flex items-center space-x-2 bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white px-4 py-2 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 ${!canSubmitToday ? 'opacity-75 cursor-not-allowed' : ''}`}
                title={canSubmitToday ? "Share your experience" : "You can submit feedback once per day"}
                data-testid="feedback-btn"
                disabled={!canSubmitToday}
              >
                <MessageCircle className="w-4 h-4" />
                <span>Give Feedback</span>
              </button>
              <button
                onClick={handleLogout}
                className="bg-white/10 text-slate-300 px-4 py-2 rounded-lg hover:bg-white/20 hover:text-white transition-colors flex items-center space-x-2 border border-white/10"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? (
                <svg className="w-6 h-6 text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6 text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="md:hidden py-4 border-t border-slate-200 bg-white">
              <div className="flex flex-col space-y-3">
                <div className="px-4 py-2 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-600">Welcome back,</p>
                  <p className="font-semibold text-slate-900">{user?.name}</p>
                </div>
                {user?.is_admin && (
                  <button
                    onClick={() => {
                      navigate('/admin');
                      setIsMenuOpen(false);
                    }}
                    className="flex items-center space-x-2 bg-purple-100 text-purple-700 px-4 py-3 rounded-lg hover:bg-purple-200 transition-colors w-full"
                  >
                    <Settings className="w-4 h-4" />
                    <span>Admin Panel</span>
                  </button>
                )}
                <button
                  onClick={() => {
                    setShowFeedbackModal(true);
                    setIsMenuOpen(false);
                  }}
                  className={`flex items-center space-x-2 bg-gradient-to-r from-green-600 to-blue-600 text-white px-4 py-3 rounded-lg font-semibold w-full ${!canSubmitToday ? 'opacity-75 cursor-not-allowed' : ''}`}
                  disabled={!canSubmitToday}
                >
                  <MessageCircle className="w-4 h-4" />
                  <span>Give Feedback</span>
                </button>
                <button
                  onClick={() => {
                    handleLogout();
                    setIsMenuOpen(false);
                  }}
                  className="flex items-center space-x-2 bg-slate-100 text-slate-700 px-4 py-3 rounded-lg hover:bg-slate-200 transition-colors w-full"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          )}
        </div>
        
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Feedback Prompt */}
        {showFeedbackPrompt && !hasRecentFeedback && (
          <div className="bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="bg-blue-100 rounded-full p-2">
                  <Star className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Loving Portfolio Auditor?</h3>
                  <p className="text-sm text-slate-600">Share your experience and help us improve!</p>
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setShowFeedbackModal(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  Give Feedback
                </button>
                <button
                  onClick={() => setShowFeedbackPrompt(false)}
                  className="text-slate-500 hover:text-slate-700 px-2"
                >
                  ✕
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Dashboard Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="dashboard-card">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Active Subscriptions</p>
                <p className="text-2xl font-bold text-slate-900" data-testid="active-subscriptions">
                  {orders.filter(order => order.status === 'completed').length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="dashboard-card">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <Download className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Downloads Available</p>
                <p className="text-2xl font-bold text-slate-900" data-testid="downloads-available">
                  {orders.filter(order => order.status === 'completed').length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="dashboard-card">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <Key className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">License Keys</p>
                <p className="text-2xl font-bold text-slate-900" data-testid="license-keys">
                  {orders.filter(order => order.status === 'completed' && order.license_key).length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Orders Table Section */}
        <div className="bg-white rounded-2xl border border-slate-200">
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-slate-900">Your Orders</h2>
                <p className="text-slate-600">Track your purchases, licenses, and downloads</p>
              </div>
              <button
                onClick={async () => {
                  setLoading(true);
                  await fetchOrders();
                  setLoading(false);
                  showNotification('success', 'Orders Refreshed', 'Your orders have been updated successfully.');
                }}
                disabled={loading}
                className={`inline-flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all ${
                  loading 
                    ? 'bg-slate-100 text-slate-400 cursor-not-allowed' 
                    : 'bg-blue-600 hover:bg-blue-700 text-white hover:scale-105'
                }`}
                title="Refresh orders"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                <span>{loading ? 'Refreshing...' : 'Refresh Orders'}</span>
              </button>
            </div>
          </div>
          
          <div className="p-6">
            {/* Search Filters */}
            {!loading && orders.length > 0 && (
              <div className="bg-slate-50 rounded-lg p-4 mb-6">
                <h4 className="text-sm font-semibold text-slate-700 mb-3">Search & Filter Orders</h4>
                <div className="grid md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Order ID</label>
                    <input
                      type="text"
                      placeholder="Search by order ID..."
                      className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={orderSearchFilters.orderId}
                      onChange={(e) => handleFilterChange('orderId', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">License Key</label>
                    <input
                      type="text"
                      placeholder="Search by license key..."
                      className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={orderSearchFilters.licenseKey}
                      onChange={(e) => handleFilterChange('licenseKey', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Payment ID</label>
                    <input
                      type="text"
                      placeholder="Search by payment ID..."
                      className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={orderSearchFilters.paymentId}
                      onChange={(e) => handleFilterChange('paymentId', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Package</label>
                    <select
                      className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={orderSearchFilters.package}
                      onChange={(e) => handleFilterChange('package', e.target.value)}
                    >
                      <option value="">All Packages</option>
                      <option value="beta">Beta Free Trial</option>
                      <option value="individual">Individual</option>
                      <option value="family">Family</option>
                    </select>
                  </div>
                </div>
                <div className="flex justify-between items-center mt-4">
                  <button
                    onClick={clearOrderFilters}
                    className="px-4 py-2 bg-slate-600 text-white rounded-md text-sm hover:bg-slate-700 transition-colors"
                  >
                    Clear Filters
                  </button>
                  <div className="text-sm text-slate-600">
                    Showing {paginatedOrdersData.orders.length} of {paginatedOrdersData.totalOrders} orders
                  </div>
                </div>
              </div>
            )}
            
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="loading-spinner w-8 h-8"></div>
              </div>
            ) : orders.length === 0 ? (
              <div className="text-center py-12">
                <CreditCard className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">No orders yet</h3>
                <p className="text-slate-600">Purchase a plan to start using Portfolio Auditor</p>
              </div>
            ) : (
              <>
                <div className="space-y-6">
                  {paginatedOrdersData.orders.map((order, index) => {
                    const isExpired = order.expires_at && new Date(order.expires_at) < new Date();
                    const planInfo = pricingPlans.find(p => p.id === order.subscription_type);
                    
                    return (
                      <div key={order.id} className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm" data-testid={`order-${order.id}`}>
                        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                          
                          {/* Order Info Section */}
                          <div className="lg:col-span-1">
                            <div className="space-y-3">
                              <div>
                                <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Order ID</label>
                                <p className="text-sm font-medium text-slate-900 mt-1">#{order.id}</p>
                              </div>
                              {order.razorpay_payment_id && (
                                <div>
                                  <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Payment ID</label>
                                  <div className="flex items-center space-x-2 mt-1">
                                    <code className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-mono flex-1">
                                      {order.razorpay_payment_id}
                                    </code>
                                    <button
                                      onClick={() => navigator.clipboard.writeText(order.razorpay_payment_id)}
                                      className="text-slate-400 hover:text-slate-600 transition-colors"
                                      title="Copy Payment ID"
                                    >
                                      <CreditCard className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              )}
                              <div>
                                <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Purchase Date</label>
                                <p className="text-sm text-slate-600 mt-1">
                                  {new Date(order.created_at).toLocaleDateString('en-IN', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric'
                                  })}
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Package & Price Section */}
                          <div className="lg:col-span-1">
                            <div className="space-y-3">
                              <div>
                                <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Package</label>
                                <div className="mt-1">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    order.subscription_type === 'beta' ? 'bg-orange-100 text-orange-800' :
                                    order.subscription_type === 'individual' ? 'bg-purple-100 text-purple-800' :
                                    order.subscription_type === 'family' ? 'bg-green-100 text-green-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {order.subscription_type === 'beta' ? 'Beta Free Trial' :
                                     order.subscription_type === 'individual' ? 'Individual' :
                                     order.subscription_type === 'family' ? 'Family' :
                                     order.subscription_type.charAt(0).toUpperCase() + order.subscription_type.slice(1)}
                                  </span>
                                </div>
                              </div>
                              <div>
                                <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Price</label>
                                <p className="text-lg font-semibold text-slate-900 mt-1">₹{(order.amount / 100).toLocaleString('en-IN')}</p>
                              </div>
                            </div>
                          </div>

                          {/* Status & Expiry Section */}
                          <div className="lg:col-span-1">
                            <div className="space-y-3">
                              <div>
                                <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Status</label>
                                <div className="mt-1">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    order.status === 'completed' && !isExpired
                                      ? 'bg-green-100 text-green-800'
                                      : order.status === 'pending'
                                      ? 'bg-yellow-100 text-yellow-800'
                                      : order.status === 'failed' 
                                      ? 'bg-red-100 text-red-800'
                                      : isExpired
                                      ? 'bg-red-100 text-red-800'
                                      : 'bg-gray-100 text-gray-800'
                                  }`}>
                                    {getStatusIcon(isExpired ? 'expired' : order.status)}
                                    {order.status === 'completed' && !isExpired ? 'Active' : 
                                     order.status === 'pending' ? 'Pending' :
                                     order.status === 'failed' ? 'Failed' :
                                     isExpired ? 'Expired' : 'Cancelled'}
                                  </span>
                                </div>
                              </div>
                              <div>
                                <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Expiry Date</label>
                                <div className={`flex items-center mt-1 ${isExpired ? 'text-red-600' : order.subscription_type === 'family' ? 'text-green-600' : 'text-slate-600'}`}>
                                  <Calendar className={`w-4 h-4 mr-1 ${isExpired ? 'text-red-400' : order.subscription_type === 'family' ? 'text-green-400' : 'text-slate-400'}`} />
                                  <span className="text-sm">
                                    {order.expires_at 
                                      ? new Date(order.expires_at).toLocaleDateString('en-IN', {
                                          year: 'numeric',
                                          month: 'short', 
                                          day: 'numeric'
                                        })
                                      : 'N/A'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* License & Actions Section */}
                          <div className="lg:col-span-1">
                            <div className="space-y-4">
                              <div>
                                <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">License Key</label>
                                <div className="flex items-center space-x-2 mt-1">
                                  {order.license_key ? (
                                    <>
                                      <code className={`px-2 py-1 rounded text-xs font-mono flex-1 ${
                                        isExpired ? 'bg-red-50 text-red-700' : 
                                        order.subscription_type === 'family' ? 'bg-green-50 text-green-700' :
                                        'bg-slate-100 text-slate-700'
                                      }`}>
                                        {order.license_key}
                                      </code>
                                      <button
                                        onClick={() => navigator.clipboard.writeText(order.license_key)}
                                        className="text-slate-400 hover:text-slate-600 transition-colors"
                                        title="Copy License Key"
                                      >
                                        <Key className="w-4 h-4" />
                                      </button>
                                    </>
                                  ) : (
                                    <span className="text-xs text-slate-500">Pending</span>
                                  )}
                                </div>
                              </div>
                              
                              {/* Action Buttons */}
                              <div className="space-y-2">
                                {/* Secure S3 Download Button */}
                                {(order.status === 'completed' && !isExpired) ? (
                                  <button
                                    onClick={() => handleSecureDownload(order.id)}
                                    disabled={downloadingOrders.has(order.id)}
                                    className={`inline-flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-lg text-white transition-colors w-full ${
                                      downloadingOrders.has(order.id) 
                                        ? 'bg-blue-400 cursor-not-allowed' 
                                        : 'bg-blue-600 hover:bg-blue-700'
                                    }`}
                                    data-testid="download-btn"
                                  >
                                    {downloadingOrders.has(order.id) ? (
                                      <>
                                        <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                                        Generating Download...
                                      </>
                                    ) : (
                                      <>
                                        <Download className="w-5 h-5 mr-2" />
                                        Download Portfolio Auditor
                                      </>
                                    )}
                                  </button>
                                ) : (
                                  <button
                                    className="inline-flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-lg text-white bg-slate-400 cursor-not-allowed w-full"
                                    title={isExpired ? "Download not available for expired license" : "Download not available"}
                                    disabled
                                  >
                                    <Download className="w-5 h-5 mr-2" />
                                    Download Portfolio Auditor
                                  </button>
                                )}
                                
                                {/* Installation Guide Link */}
                                <a
                                  href="/installation-guide"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center justify-center px-4 py-3 border border-indigo-300 text-sm font-medium rounded-lg text-indigo-700 bg-indigo-50 hover:bg-indigo-100 transition-colors w-full"
                                >
                                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                  </svg>
                                  Installation Guide
                                </a>
                                
                                {/* Live Chat Button */}
                                <button
                                  onClick={() => window.Tawk_API && window.Tawk_API.maximize()}
                                  className="inline-flex items-center justify-center px-4 py-3 border border-blue-300 text-sm font-medium rounded-lg text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors w-full"
                                  data-testid="live-chat-btn"
                                >
                                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                  </svg>
                                  Live Chat Support
                                </button>
                                
                                {/* Email Support Button */}
                                <a
                                  href={`mailto:${process.env.REACT_APP_SUPPORT_EMAIL || 'support@portfolioauditor.com'}`}
                                  className="inline-flex items-center justify-center px-4 py-3 border border-slate-300 text-sm font-medium rounded-lg text-slate-700 bg-white hover:bg-slate-50 transition-colors w-full"
                                  data-testid="email-support-btn"
                                >
                                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                  </svg>
                                  Email us for Support
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Subscription Management Section - For Individual/Family/Annual plans */}
                        {(order.subscription_type === 'individual' || order.subscription_type === 'family' || order.subscription_type === 'annual') && (() => {
                          const subscription = subscriptions.find(sub => sub.order_id === order.id);
                          
                          if (!subscription) return null;
                          
                          const isGracePeriod = subscription.grace_period_end_date && 
                            new Date(subscription.grace_period_end_date) > new Date();
                          const isCancelled = subscription.subscription_status === 'cancelled';

                          return (
                            <div className="mt-6 pt-6 border-t border-slate-200">
                              <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4">
                                <div className="flex items-center justify-between mb-4">
                                  <h4 className="text-sm font-semibold text-slate-900 flex items-center">
                                    <RefreshCw className="w-4 h-4 mr-2 text-blue-600" />
                                    Subscription Management
                                  </h4>
                                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                    isCancelled ? 'bg-red-100 text-red-800' :
                                    isGracePeriod ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-green-100 text-green-800'
                                  }`}>
                                    {isCancelled ? 'Cancelled' : 
                                     isGracePeriod ? 'Grace Period' : 
                                     'Active'}
                                  </span>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                  {/* Next Billing Date */}
                                  {subscription.next_billing_date && !isCancelled && (
                                    <div className="bg-white rounded-lg p-3 border border-blue-200">
                                      <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Next Billing Date</label>
                                      <div className="flex items-center mt-1">
                                        <Calendar className="w-4 h-4 mr-2 text-blue-600" />
                                        <span className="text-sm font-semibold text-slate-900">
                                          {new Date(subscription.next_billing_date).toLocaleDateString('en-IN', {
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric'
                                          })}
                                        </span>
                                      </div>
                                    </div>
                                  )}

                                  {/* Cancel Subscription Button - Moved next to Next Billing Date */}
                                  {!isCancelled && (
                                    <div className="bg-white rounded-lg p-3 border border-red-200 flex items-center justify-center">
                                      <button
                                        onClick={() => handleCancelClick(subscription)}
                                        className="w-full inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg text-red-700 bg-red-50 hover:bg-red-100 transition-colors"
                                      >
                                        <XCircle className="w-4 h-4 mr-2" />
                                        Cancel Subscription
                                      </button>
                                    </div>
                                  )}

                                  {/* Grace Period Warning */}
                                  {isGracePeriod && (
                                    <div className="bg-yellow-50 rounded-lg p-3 border border-yellow-200">
                                      <label className="text-xs font-medium text-yellow-700 uppercase tracking-wider flex items-center">
                                        <AlertCircle className="w-3 h-3 mr-1" />
                                        Grace Period Ends
                                      </label>
                                      <div className="flex items-center mt-1">
                                        <Clock className="w-4 h-4 mr-2 text-yellow-600" />
                                        <span className="text-sm font-semibold text-yellow-900">
                                          {new Date(subscription.grace_period_end_date).toLocaleDateString('en-IN', {
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric'
                                          })}
                                        </span>
                                      </div>
                                      <p className="text-xs text-yellow-700 mt-2">
                                        Update payment method to avoid license expiration
                                      </p>
                                    </div>
                                  )}

                                  {/* Auto-Renewal Toggle - Hidden for now */}
                                  {!isCancelled && (
                                    <div className="bg-white rounded-lg p-3 border border-blue-200 hidden">
                                      <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Auto-Renewal</label>
                                      <div className="flex items-center justify-between mt-2">
                                        <span className="text-sm text-slate-700">
                                          {subscription.auto_renew_enabled ? 'Enabled' : 'Disabled'}
                                        </span>
                                        <button
                                          onClick={() => handleToggleAutoRenew(order.id, subscription.auto_renew_enabled)}
                                          disabled={togglingAutoRenew.has(order.id)}
                                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                            subscription.auto_renew_enabled ? 'bg-blue-600' : 'bg-gray-300'
                                          } ${togglingAutoRenew.has(order.id) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        >
                                          <span
                                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                              subscription.auto_renew_enabled ? 'translate-x-6' : 'translate-x-1'
                                            }`}
                                          />
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>

                                {/* Cancellation Info */}
                                {isCancelled && (
                                  <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                                    <p className="text-sm text-red-800">
                                      <strong>Subscription Cancelled:</strong> Your license will remain active until{' '}
                                      {subscription.expires_at 
                                        ? new Date(subscription.expires_at).toLocaleDateString('en-IN', {
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric'
                                          })
                                        : 'the end of the current billing period'}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })}
                </div>
                
                {/* Pagination Controls */}
                {!loading && paginatedOrdersData.totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 sm:px-6 mt-6">
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center text-sm text-slate-700">
                        <span>
                          {`Showing ${Math.min((ordersPagination.currentPage - 1) * ordersPagination.itemsPerPage + 1, paginatedOrdersData.totalOrders)} to ${Math.min(ordersPagination.currentPage * ordersPagination.itemsPerPage, paginatedOrdersData.totalOrders)} of ${paginatedOrdersData.totalOrders} orders`}
                        </span>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleOrderPageChange(ordersPagination.currentPage - 1)}
                          disabled={ordersPagination.currentPage === 1}
                          className="px-3 py-1 border border-slate-300 rounded-md text-sm font-medium text-slate-500 bg-white hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Previous
                        </button>
                        
                        {/* Page Numbers */}
                        <div className="flex items-center space-x-1">
                          {[...Array(paginatedOrdersData.totalPages)].map((_, index) => {
                            const page = index + 1;
                            return (
                              <button
                                key={page}
                                onClick={() => handleOrderPageChange(page)}
                                className={`px-3 py-1 border rounded-md text-sm font-medium ${
                                  ordersPagination.currentPage === page
                                    ? 'border-blue-500 bg-blue-50 text-blue-600'
                                    : 'border-slate-300 text-slate-500 bg-white hover:bg-slate-50'
                                }`}
                              >
                                {page}
                              </button>
                            );
                          })}
                        </div>
                        
                        <button
                          onClick={() => handleOrderPageChange(ordersPagination.currentPage + 1)}
                          disabled={ordersPagination.currentPage === paginatedOrdersData.totalPages}
                          className="px-3 py-1 border border-slate-300 rounded-md text-sm font-medium text-slate-500 bg-white hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Purchase Plans Section - Always visible for new orders */}
        <div className="mt-8" data-section="pricing">
          <div className="bg-white rounded-2xl p-8 border border-slate-200">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              {orders.filter(order => order.status === 'completed').length === 0 ? 'Choose Your Plan' : 'Purchase Additional License'}
            </h2>
            <p className="text-slate-600 mb-6">
              {orders.filter(order => order.status === 'completed').length === 0 
                ? 'Start analyzing your portfolio with our powerful tools' 
                : 'Need another license or want to upgrade? Choose from our available plans'}
            </p>
            
            <div className="grid md:grid-cols-3 gap-6">
              {pricingPlans.filter(plan => plan.id !== 'family').map((plan) => (
                <div key={plan.id} className={`relative bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border-2 ${
                  plan.beta ? 'border-blue-500 transform scale-105' :
                  plan.id === 'individual' ? 'border-green-500' :
                  plan.popular ? 'border-blue-600 transform scale-105' : 'border-slate-200'
                } p-6 flex flex-col h-full`}>
                  
                  {/* Tag/Badge */}
                  <div className={`absolute -top-3 left-1/2 transform -translate-x-1/2 px-3 py-1 rounded-full text-xs font-bold text-white ${
                    plan.beta ? 'bg-blue-600' :
                    plan.id === 'individual' ? 'bg-green-600' :
                    plan.popular ? 'bg-blue-600' : 'bg-slate-600'
                  }`}>
                    {plan.tag}
                  </div>
                  
                  <div className="flex-grow">
                    {/* Plan Name */}
                    <h3 className="text-xl font-bold text-slate-900 mb-3 mt-3 text-center">{plan.name}</h3>
                    
                    {/* Description */}
                    <p className="text-slate-600 mb-4 text-center text-sm leading-relaxed" 
                       dangerouslySetInnerHTML={{__html: plan.description.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')}}></p>
                    
                    {/* Price */}
                    <div className="mb-4 text-center">
                      {plan.beta ? (
                        <>
                          <span className="text-4xl font-bold text-blue-600">FREE</span>
                          <span className="text-slate-500 ml-2">{plan.period}</span>
                        </>
                      ) : (
                        <>
                          <span className={`text-4xl font-bold ${
                            plan.id === 'individual' ? 'text-green-600' :
                            plan.popular ? 'text-blue-600' : 'text-slate-900'
                          }`}>₹{plan.price}</span>
                          <span className="text-slate-500 ml-2">{plan.period}</span>
                        </>
                      )}
                    </div>

                    {/* Savings Badge for Family Plan */}
                    {plan.savings && (
                      <div className="mb-4 text-center">
                        <span className="inline-block bg-gradient-to-r from-yellow-400 to-orange-400 text-slate-900 text-xs font-bold px-2 py-1 rounded-full">
                          {plan.savings}
                        </span>
                      </div>
                    )}
                    
                    {/* Feature Sections */}
                    <div className="space-y-4 mb-6 flex-grow">
                      {plan.featureSections.map((section, sectionIdx) => (
                        <div key={sectionIdx}>
                          <h4 className="text-xs font-bold text-slate-900 mb-2 uppercase tracking-wide">
                            {section.title}
                          </h4>
                          <ul className="space-y-1">
                            {section.features.map((feature, featureIdx) => (
                              <li key={featureIdx} className="flex items-start text-xs text-slate-600">
                                <CheckCircle className={`w-3 h-3 mr-2 mt-0.5 flex-shrink-0 ${
                                  plan.beta ? 'text-blue-500' :
                                  plan.id === 'individual' ? 'text-green-500' :
                                  plan.popular ? 'text-blue-500' : 'text-green-500'
                                }`} />
                                <span dangerouslySetInnerHTML={{__html: feature.replace(/\*\*(.*?)\*\*/g, '<strong class="text-slate-900">$1</strong>')}}></span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* CTA Button */}
                  <div className="mt-auto">
                    <button
                      onClick={() => handlePurchase(plan.id)}
                      disabled={paymentLoading && selectedPlan === plan.id}
                      className={`w-full py-3 px-4 rounded-xl font-semibold text-sm transition-all duration-300 ${
                        plan.beta
                          ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl'
                          : plan.id === 'individual'
                          ? 'bg-green-600 hover:bg-green-700 text-white shadow-lg hover:shadow-xl' 
                          : plan.popular
                          ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl'
                          : 'bg-slate-600 hover:bg-slate-700 text-white shadow-lg hover:shadow-xl'
                      } disabled:opacity-50`}
                      data-testid={`purchase-${plan.id}-btn`}
                    >
                      {paymentLoading && selectedPlan === plan.id ? 'Processing...' : 
                       plan.buttonText || (plan.beta ? 'Start Free Trial' : 'Purchase')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>

      {/* Cancel Subscription Confirmation Modal */}
      {showCancelModal && selectedSubscription && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Cancel Subscription</h3>
                <p className="text-sm text-slate-600">This action cannot be undone</p>
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-red-800 mb-2">
                <strong>Order ID:</strong> #{selectedSubscription.order_id}
              </p>
              {selectedSubscription.license_key && (
                <p className="text-sm text-red-800 mb-2">
                  <strong>License:</strong> {selectedSubscription.license_key}
                </p>
              )}
              <p className="text-sm text-red-800">
                Your subscription will remain active until{' '}
                {selectedSubscription.next_billing_date 
                  ? new Date(selectedSubscription.next_billing_date).toLocaleDateString('en-IN', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })
                  : 'the end of the current billing period'}
              </p>
            </div>

            <div className="space-y-3">
              <button
                onClick={handleCancelSubscription}
                className="w-full inline-flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 transition-colors"
              >
                <XCircle className="w-5 h-5 mr-2" />
                Yes, Cancel Subscription
              </button>
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setSelectedSubscription(null);
                }}
                className="w-full inline-flex items-center justify-center px-4 py-3 border border-slate-300 text-sm font-medium rounded-lg text-slate-700 bg-white hover:bg-slate-50 transition-colors"
              >
                Keep Subscription
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        onSubmit={handleFeedbackSubmit}
      />
    </div>
  );
};

export default Dashboard;